import {ContractInfo} from 'components/contract-info/ContractInfo'
import {Select} from 'components/select/Select'
import {connectService} from 'components/service/connectService'
import {useFormik} from 'formik'
import {useConfirm} from 'hooks/useConfirm'
import {keys, pick} from 'lodash'
import {Sections} from 'modules/core/tenant/sections/Sections'
import {NavigationContext} from 'modules/dashboard/NavigationContext'
import {useContext, useEffect, useMemo} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import {ContractTypes, Roles, hasPermission} from 'utility/utility'
import {ResellerPrototype, ResellerSchema} from './schema'
import {ValidationError} from 'components/validation-error/ValidationError'

export const Reseller = connectService(
  props => {
    const confirm = useConfirm()
    const navigate = useNavigate()
    const {setTitle} = useContext(NavigationContext)
    const {edit, data, onSave, remove} = props

    const params = useParams()

    useEffect(() => {
      setTitle(data?.name || 'Create Reseller')
    }, [])

    const allowDistributorUpdate = useMemo(() => {
      return hasPermission(Roles.superAdmin) && !params.distributor
    }, [params])

    const handleRemove = async () => {
      await remove(data._id).then()
      navigate(-1)
    }

    const initialData = useMemo(() => {
      return pick(data, keys(ResellerPrototype))
    }, [data, edit])

    const formik = useFormik({
      initialValues: edit
        ? initialData
        : {
            ...ResellerPrototype
          },
      validationSchema: ResellerSchema,
      onSubmit: async data => {
        if (!data.distributor) {
          delete data.distributor
        }

        if (params.distributor && !allowDistributorUpdate) {
          data.distributor = params.distributor
        }

        if (data.contractType === ContractTypes.FreePartnerAccount) {
          delete data.contractExpiry
          delete data.trialExpiry
        }

        await onSave({data})
        navigate(-1)
      },
      enableReinitialize: true
    })

    return (
      <>
        {allowDistributorUpdate && (
          <>
            <div className="form-group row">
              <label className="col-sm-4 col-lg-5 col-form-label">Distributor</label>
              <div className="col-sm-8 col-lg-7">
                <Select
                  service="distributor"
                  title="Select Distributor"
                  value={formik.values?.distributor}
                  onChange={e => formik.setFieldValue('distributor', e.target.value)}
                />
              </div>
            </div>
            <hr />
          </>
        )}

        <div className="form-group row">
          <label className="col-sm-4 col-lg-5 col-form-label">Reseller name</label>
          <div className="col-sm-8 col-lg-7">
            <input
              type="text"
              className="form-control"
              placeholder="Reseller name"
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
            />
            <ValidationError formik={formik} property="name" />
          </div>
        </div>
        <div className="form-group row">
          <label className="col-sm-4 col-lg-5 col-form-label">Address (optional)</label>
          <div className="col-sm-8 col-lg-7">
            <input
              type="text"
              className="form-control"
              placeholder="Address"
              name="address"
              value={formik.values.address}
              onChange={formik.handleChange}
            />
            <ValidationError formik={formik} property="address" />
          </div>
        </div>
        <div className="form-group row">
          <label className="col-sm-4 col-lg-5 col-form-label">Postcode (optional)</label>
          <div className="col-sm-8 col-lg-7">
            <input
              type="text"
              className="form-control"
              placeholder="Postcode"
              name="postalCode"
              value={formik.values.postalCode}
              onChange={formik.handleChange}
            />
            <ValidationError formik={formik} property="postalCode" />
          </div>
        </div>
        <div className="form-group row">
          <label className="col-sm-4 col-lg-5 col-form-label">Phone number (optional)</label>
          <div className="col-sm-8 col-lg-7">
            <input
              type="text"
              className="form-control"
              placeholder="Phone number"
              name="phone"
              value={formik.values.phone}
              onChange={formik.handleChange}
            />
            <ValidationError formik={formik} property="phone" />
          </div>
        </div>

        <div className="form-group row">
          <label className="col-sm-4 col-lg-5 col-form-label">Account Number from ZOHO</label>
          <div className="col-sm-8 col-lg-7">
            <input
              type="text"
              placeholder="Account Number from ZOHO"
              className="form-control"
              name="accountNumber"
              value={formik.values.accountNumber}
              onChange={formik.handleChange}
            />
            <ValidationError formik={formik} property="accountNumber" />
          </div>
        </div>

        <ContractInfo
          contractType={formik.values.contractType}
          contractTypeProps={{name: 'contractType'}}
          onContractTypeChange={formik.handleChange}
          trialExpiry={formik.values.trialExpiry}
          onTrialExpiryChange={value => {
            formik.setFieldValue('trialExpiry', value)
            formik.setFieldValue('contractExpiry', undefined)
          }}
          contractExpiry={formik.values.contractExpiry}
          onContractExpiryChange={value => {
            formik.setFieldValue('trialExpiry', undefined)
            formik.setFieldValue('contractExpiry', value)
          }}
        >
          {((formik.touched.contractType && formik.errors.contractType) ||
            (formik.touched.trialExpiry && formik.errors.trialExpiry) ||
            (formik.touched.contractExpiry && formik.errors.contractExpiry)) && (
            <small className="form-text text-danger">
              {formik.errors.accountNumber}
              {formik.errors.trialExpiry}
              {formik.errors.contractExpiry}
            </small>
          )}
        </ContractInfo>

        {edit && (
          <Sections
            AdministratorTabProps={{
              entity: 'reseller'
            }}
          />
        )}

        <div className="vw-popup-box__inner__body__btns">
          <button className="btn btn-primary" onClick={formik.handleSubmit}>
            Save reseller
          </button>
          {edit && (
            <button
              className="btn btn-danger"
              onClick={() => {
                confirm({
                  title: `Are you sure?`,
                  message: `Are you sure you want to remove reseller ${data.name}? Once deleted, all linked business and users will be removed as well. This step can not be undo.`,
                  onConfirm: handleRemove
                })
              }}
            >
              Remove Distributor
            </button>
          )}
          <button className="btn btn-secondary" onClick={() => navigate(-1)}>
            Cancel
          </button>
        </div>
      </>
    )
  },
  'reseller',
  {
    paramsAsQueryParams: ['distributor']
  }
)
